import React from 'react';
import deepcopy from 'deepcopy';
import { Link } from 'react-router-dom';
import classnames from 'classnames';
import Status from '@hobs/components/Status';
import Icon from '@hobs/components/Icon';
import ObjectPath from 'object-path';
import { CellDate, CellDateTime } from '@hobs/components/Grid/CellDate';
import { ServiceRequestNavContext } from '@hobs/components/ServiceRequestNavContext';
import Indicator from '@hobs/components/Indicator';
import {
  USER_ROLES,
  DEFAULT_STATUS_FILTER,
  INDICATIONS,
  SERVICE_REQUEST_NAVIGATION_ORIGIN,
  GRID_CUSTOM_FILTER_FIELDS,
  PARTY_TYPE,
  DATE_FORMAT
} from '@hobs/utils/constants';
import PropTypes from 'prop-types';
import ShortId from 'shortid';
import {
  withDataStateLocalOverrides,
  withGridLocalOverrides,
  onUserResetSettings
} from '@hobs/utils/grid/overrides';
import Money from '@hobs/components/Money';
import Discussion from '@hobs/containers/Discussion';
import { formatLocal, parse } from '@hobs/utils/formatWithTimeZone';
import './ServiceRequests.scss';
import { expandDataItem } from '@hobs/utils/expandItem';
import Quantity from '@hobs/components/Quantity';
import { Icon as BpIcon } from '@blueprintjs/core';
import Tooltip from '@hobs/components/Tooltip';

const INDICATION_TRANSPORTATION_FIELD =
  'Indications.TransportationInformationMissing';

class ServiceRequests extends React.PureComponent {
  NotificationsQueryFilterName = `NotificationsQueryFilter`;
  corStatus = [];

  constructor(props) {
    super(props);
    const { t, hasUserPermission } = this.props;

    this.exportRef = React.createRef();
    this.grid = React.createRef();

    this.defaultGridColumns = {
      Indications: {
        title: t('Indications'),
        columnMenu: true,
        default: true,
        sortable: false,
        visible: true,
        minResizableWidth: '46px',
        headerCell: () => (
          <div>
            <span className="indicator-bubble"></span>
          </div>
        ),
        cell: ({ dataItem }) => {
          const indications = this.getIndications(dataItem.Indications);
          const hasIndication = dataItem.HasIndication;
          return (
            <td valign="middle">
              {indications.length > 0 && hasIndication && (
                <Indicator
                  message={indications.map(indication => (
                    <div key={ShortId.generate()}>
                      {INDICATIONS[indication]}
                    </div>
                  ))}
                  key={ShortId.generate()}
                />
              )}
            </td>
          );
        },
        customFilter: {
          data: [
            {
              name: t('All'),
              default: true,
              value: ''
            },
            {
              name: t('None'),
              default: false,
              value: false,
              field: INDICATION_TRANSPORTATION_FIELD
            },
            ...this.getIndicationColumnFilter()
          ]
        }
      },
      RequestNumber: {
        minResizableWidth: '70px',
        title: t('Req.'),
        columnMenu: true,
        width: '84px',
        cell: ({ dataItem }) => {
          const editURI = `/${
            dataItem.IsCustom ? 'custom-' : ''
          }service-request/${this.props.match.params.partyId}/${
            dataItem.ServiceRequestId
          }`;

          return (
            <td>
              {dataItem.RequestNumber && (
                <ServiceRequestNavContext.Consumer>
                  {nav => (
                    <Link
                      to={editURI}
                      title={dataItem.RequestNumber}
                      onClick={() => {
                        nav.setPreviousGridPosition(
                          this.state.dataState,
                          window.pageYOffset,
                          this.state.selectedAnnex,
                          this.state.quickSearchValue,
                          SERVICE_REQUEST_NAVIGATION_ORIGIN.RequestedServices,
                          this.state.selectedUnit
                        );
                      }}
                    >
                      {dataItem.RequestNumber}
                    </Link>
                  )}
                </ServiceRequestNavContext.Consumer>
              )}

              {!dataItem.RequestNumber && <>-</>}
            </td>
          );
        },
        default: true,
        visible: true
      },
      HasAttachments: {
        minResizableWidth: '33px',
        title: t('Attachment'),
        cell: ({ dataItem }) => (
          <td colSpan="1">
            {dataItem.HasAttachments && (
              <Icon icon="attachment" className="service-requests-view--icon" />
            )}
          </td>
        ),
        headerCell: () => (
          <Icon icon="attachment" className="service-requests-view--icon" />
        ),
        default: false,
        visible: false,
        xls: false,
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            {
              name: t('Yes'),
              value: true
            },
            {
              name: t('No'),
              value: false
            }
          ]
        },
        columnMenu: true
      },
      AdditionalInfo: {
        title: t('Add. info.'),
        minResizableWidth: '45px',
        default: true,
        visible: true,
        xls: false,
        cell: ({ dataItem }) => (
          <td>
            {dataItem.Indications.IsEarlyConfirmationRequired && (
              <Tooltip
                tooltip={'Early confirmation required'}
                position="bottom"
              >
                <BpIcon icon="warning-sign" color="#b50026" />
              </Tooltip>
            )}
            {dataItem.AdditionalInfo && (
              <ServiceRequestNavContext.Consumer>
                {nav => (
                  <Link
                    to={`/service-request/${this.props.match.params.partyId}/${dataItem.ServiceRequestId}/change-history`}
                    onClick={() => {
                      nav.setPreviousGridPosition(
                        this.state.dataState,
                        window.pageYOffset,
                        this.state.selectedAnnex,
                        this.state.quickSearchValue,
                        SERVICE_REQUEST_NAVIGATION_ORIGIN.RequestedServices,
                        this.state.selectedUnit
                      );
                      nav.setServiceRequestId(dataItem.ServiceRequestId);
                    }}
                  >
                    <Tooltip
                      tooltip={
                        dataItem.LatestTicketAction &&
                        `${dataItem.LatestTicketAction}${formatLocal(
                          dataItem.LatestTicketActionDate,
                          DATE_FORMAT
                        )})`
                      }
                      position="top"
                    >
                      <Icon
                        className={classnames(
                          'info-indication',
                          {
                            active: dataItem.AdditionalInfoActive
                          },
                          {
                            rejected: dataItem.AdditionalInfoRejected
                          }
                        )}
                        icon="InfoSmall"
                      />
                    </Tooltip>
                  </Link>
                )}
              </ServiceRequestNavContext.Consumer>
            )}
          </td>
        )
      },
      StatusId: {
        title: t('Status'),
        width: 103,
        columnMenu: true,
        cell: ({ dataItem }) => (
          <td>
            <Status
              name={dataItem.StatusGridName}
              className="service-requests-view--status"
              type={dataItem.StatusId}
            />
          </td>
        ),
        default: true,
        visible: true
      },
      Service: {
        title: t('Service'),
        columnMenu: true,
        default: true,
        visible: true
      },
      Description: {
        minResizableWidth: '80px',
        title: t('Description'),
        columnMenu: true,
        default: false,
        visible: false
      },
      Group: {
        title: t('Group'),
        columnMenu: true,
        default: true,
        visible: true,
        customFilter: {
          filter: 'contains',
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            ...this.getColumnFilterValuesAsStringNoId(
              GRID_CUSTOM_FILTER_FIELDS.groups,
              'GroupNames'
            )
          ]
        }
      },
      AnnexId: {
        title: t('Annex'),
        cell: ({ dataItem }) => <td>{dataItem.Annex}</td>,
        columnMenu: true,
        default: false,
        visible: false,
        xlsField: 'Annex'
      },
      RequestedQuantity: {
        title: t('Qty'),
        columnMenu: true,
        default: true,
        visible: true,
        filter: 'numeric',
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Quantity amount={dataItem.RequestedQuantity} />
            </td>
          );
        }
      },
      IsCalculatedByDays: {
        title: t('Calculated by days'),
        default: true,
        visible: true,
        columnMenu: true,
        cell: ({ dataItem }) => {
          return <td>{dataItem.IsCalculatedByDays ? t('Yes') : t('No')}</td>;
        },
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            {
              name: t('Yes'),
              value: true
            },
            {
              name: t('No'),
              value: false
            }
          ]
        }
      },
      TotalQuantity: {
        title: t('Total q-ty'),
        filter: 'numeric',
        columnMenu: true,
        default: true,
        visible: true,
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Quantity amount={dataItem.TotalQuantity} />
            </td>
          );
        }
      },
      DeliveryQuantity: {
        minResizableWidth: '80px',
        title: t('Delivery Qty'),
        filter: 'numeric',
        columnMenu: true,
        default: true,
        visible: true,
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Quantity amount={dataItem.DeliveryQuantity} />
            </td>
          );
        }
      },
      IsDeliveryFinalized: {
        title: t('Delivery finalized'),
        default: false,
        visible: false,
        columnMenu: true,
        cell: ({ dataItem }) => {
          return <td>{dataItem.IsDeliveryFinalized ? t('Yes') : t('No')}</td>;
        },
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            {
              name: t('Yes'),
              value: true
            },
            {
              name: t('No'),
              value: false
            }
          ]
        }
      },
      UnitOfMeasureId: {
        title: t('UoM'),
        cell: ({ dataItem }) => <td>{dataItem.UnitOfMeasure}</td>,
        columnMenu: true,
        default: true,
        visible: true,
        xlsField: 'UnitOfMeasure'
      },
      UnallocatedQuantity: {
        minResizableWidth: '80px',
        title: t('Unall qty'),
        filter: 'numeric',
        columnMenu: true,
        default: false,
        visible: false,
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Quantity amount={dataItem.UnallocatedQuantity} />
            </td>
          );
        }
      },
      Region: {
        title: t('Region'),
        columnMenu: true,
        default: false,
        visible: false
      },
      Location: {
        title: t('Location'),
        columnMenu: true,
        default: true,
        visible: true
      },
      LocationComment: {
        title: t('Location comment'),
        columnMenu: true,
        default: false,
        visible: false
      },
      LocationTo: {
        title: t('Delivery location'),
        columnMenu: true,
        default: true,
        visible: false
      },
      LocationCommentTo: {
        title: t('Delivery location comment'),
        columnMenu: true,
        default: false,
        visible: false
      },
      Site: {
        title: t('Site'),
        columnMenu: true,
        default: false,
        visible: false
      },
      Unit: {
        title: t('Unit'),
        default: true,
        columnMenu: true,
        visible: true
      },
      UnitId: {
        default: false,
        visible: false
      },
      DateFrom: {
        title: t('From date'),
        columnMenu: true,
        minResizableWidth: '85px',
        filter: 'date',
        cell: CellDate,
        default: true,
        visible: true
      },
      DateTo: {
        title: t('To date'),
        columnMenu: true,
        minResizableWidth: '85px',
        filter: 'date',
        cell: CellDate,
        default: true,
        visible: true
      },
      Days: {
        minResizableWidth: '60px',
        title: t('Days'),
        filter: 'numeric',
        default: true,
        sortable: true,
        visible: true,
        columnMenu: true
      },
      UnitPrice: {
        title: t('Unit price'),
        columnMenu: true,
        filter: 'numeric',
        default: false,
        visible: false,
        xls: false,
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Money amount={dataItem.UnitPrice} />
            </td>
          );
        }
      },
      EstimatedTotalCost: {
        minResizableWidth: '100px',
        title: t('Est. cost'),
        filter: 'numeric',
        sortable: false,
        default: true,
        visible: true,
        xls: false,
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Money amount={dataItem.EstimatedTotalCostFrom} />
              {dataItem.EstimatedTotalCostFrom !==
              dataItem.EstimatedTotalCostTo ? (
                <>
                  {' '}
                  - <Money amount={dataItem.EstimatedTotalCostTo} />
                </>
              ) : (
                ''
              )}
            </td>
          );
        }
      },
      EstimatedGrossAmount: {
        minResizableWidth: '60px',
        title: t('Est. gross amount'),
        default: true,
        visible: true,
        filter: 'numeric',
        sortable: false,
        xls: false,
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Money amount={dataItem.EstimatedGrossAmountFrom} />
              {dataItem.EstimatedGrossAmountFrom !==
              dataItem.EstimatedGrossAmountTo ? (
                <>
                  {' '}
                  - <Money amount={dataItem.EstimatedGrossAmountTo} />
                </>
              ) : (
                ''
              )}
            </td>
          );
        }
      },
      ActualCost: {
        minResizableWidth: '60px',
        title: t('Actual cost'),
        columnMenu: true,
        default: true,
        visible: true,
        filter: 'numeric',
        xls: false,
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Money amount={dataItem.ActualCost} />
            </td>
          );
        }
      },
      ActualGrossAmount: {
        minResizableWidth: '60px',
        title: t('Act. gross amount'),
        columnMenu: true,
        default: true,
        visible: true,
        filter: 'numeric',
        xls: false,
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Money amount={dataItem.ActualGrossAmount} />
            </td>
          );
        }
      },
      IsPaid: {
        title: t('Is Paid'),
        default: false,
        visible: false,
        columnMenu: true,
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            {
              name: t('Yes'),
              value: true
            },
            {
              name: t('No'),
              value: false
            }
          ]
        },
        cell: ({ dataItem }) => {
          return <td>{dataItem.IsPaid ? t('Yes') : t('No')}</td>;
        }
      },
      ActivityName: {
        title: t('Activity'),
        columnMenu: true,
        default: false,
        visible: false
      },
      IsEarlyConfirmationRequired: {
        title: t('Is Early Confirmation Required'),
        default: false,
        visible: false,
        columnMenu: true,
        cell: ({ dataItem }) => {
          return (
            <td>
              {dataItem.Indications &&
              dataItem.Indications.IsEarlyConfirmationRequired
                ? t('Yes')
                : t('No')}
            </td>
          );
        },
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              value: '',
              field: 'Indications.IsEarlyConfirmationRequired'
            },
            {
              name: t('Yes'),
              value: true,
              field: 'Indications.IsEarlyConfirmationRequired'
            },
            {
              name: t('No'),
              value: false,
              field: 'Indications.IsEarlyConfirmationRequired'
            }
          ]
        }
      },
      IsExcludedFromFsorConfirmation: {
        title: t('Is Excluded From Fsor Confirmation'),
        default: false,
        columnMenu: true,
        visible: false,
        cell: ({ dataItem }) => {
          return (
            <td>
              {dataItem.IsExcludedFromFsorConfirmation ? t('Yes') : t('No')}
            </td>
          );
        },
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            {
              name: t('Yes'),
              value: true
            },
            {
              name: t('No'),
              value: false
            }
          ]
        }
      }
    };

    if (!hasUserPermission(USER_ROLES.READONLY)) {
      Object.assign(this.defaultGridColumns, {
        DiscussionStatus: {
          title: t('Disc.'),
          default: true,
          columnMenu: true,
          visible: true,
          xls: false,
          cell: ({ dataItem }) => {
            return (
              <td>
                <Link to={this.getDiscussionPath(dataItem.ServiceRequestId)}>
                  <Discussion
                    serviceRequestId={dataItem.ServiceRequestId}
                    status={dataItem.DiscussionStatus}
                    iconOnly={true}
                    initials={t('SN')}
                    onDiscussionClose={this.queryOData}
                    isSeen={dataItem.IsLastDiscussionSeen}
                  />
                </Link>
              </td>
            );
          },
          customFilter: {
            data: [
              {
                name: t('Select all'),
                default: true,
                value: ''
              },
              {
                name: t('Unread messages'),
                value: 1
              },
              {
                name: t('Read messages'),
                value: 2
              },
              {
                name: t('No messages'),
                value: 3
              }
            ]
          }
        }
      });
    }

    Object.assign(this.defaultGridColumns, {
      CreationDate: {
        title: t('Creation date'),
        columnMenu: true,
        default: false,
        visible: false,
        filter: 'date',
        cell: CellDateTime
      },
      LastChangeDate: {
        title: t('Last change'),
        columnMenu: true,
        default: false,
        visible: false,
        filter: 'date',
        cell: CellDateTime
      },
      DeliveryComment: {
        title: t('Delivered comment'),
        columnMenu: true,
        default: false,
        visible: false
      },
      Comment: {
        title: t('Comment'),
        columnMenu: true,
        default: false,
        visible: false
      },
      ResponseComment: {
        title: t('Response comment'),
        columnMenu: true,
        default: false,
        visible: false
      }
    });

    this.defaultDataState = {
      take: 20,
      skip: 0,
      filter: {
        logic: 'and',
        filters: [DEFAULT_STATUS_FILTER]
      },
      sort: [{ field: 'RequestNumber', dir: 'desc' }]
    };

    this.state = {
      XLSoData: [],
      filters: {},
      selectedUnit: '',
      selectedAnnex: null,
      dataState: withDataStateLocalOverrides(
        deepcopy(this.defaultDataState),
        this.LSdataStateName
      ),
      showConfirmDialog: false,
      gridKey: ShortId.generate(),
      columns: withGridLocalOverrides(
        deepcopy(this.defaultGridColumns),
        this.LSGridPropsName
      ),
      isConfirmIsorDisabled: false,
      isCreateSrDisabled: false,
      previousWindowScrollOffset: null,
      selectedRows: {},
      isAllSelected: false,
      IsExerciseSelectionDialogOpen: false,
      IsServiceRequestCopyDialogOpen: false,
      selectedExerciseToCopy: {}
    };
  }

  LSdataStateName = `ServiceRequests${this.props.user.CurrentExerciseId}.dataState`;
  LSGridPropsName = `ServiceRequests${this.props.user.CurrentExerciseId}.grid.props`;

  getServiceRequestsUrl() {
    return `ServiceRequestQuery(${this.props.match.params.partyId})`;
  }

  getIndications(indications) {
    return Object.keys(indications).filter(key => {
      return indications[key];
    });
  }

  getIndicationColumnFilter() {
    const { gridSelections } = this.props;
    if (gridSelections && gridSelections.indicationTypes) {
      return gridSelections.indicationTypes.map(filterValue => {
        return {
          name: filterValue.title,
          value: true,
          field: `Indications.${filterValue.key}`
        };
      });
    }

    return [];
  }

  roundNumber(value) {
    return parseFloat(parseFloat(value).toFixed(2));
  }

  getDisplayData() {
    if (this.props.oData.data.length > 0) {
      const displayData = [].concat(this.props.oData.data);
      displayData.map(item => {
        const rowItem = this.state.selectedRows[item.ServiceRequestId];
        item.selectedField = rowItem
          ? !this.state.isAllSelected
          : this.state.isAllSelected;
      });
      displayData.push({
        footer: true,
        FooterTotalActualCost: this.roundNumber(
          displayData[0].FooterTotalActualCost
        ),
        FooterTotalEstimatedCostFrom: this.roundNumber(
          displayData[0].FooterTotalEstimatedCostFrom
        ),
        FooterTotalEstimatedCostTo: this.roundNumber(
          displayData[0].FooterTotalEstimatedCostTo
        ),
        FooterTotalEstimatedGrossAmountFrom: this.roundNumber(
          displayData[0].FooterTotalEstimatedGrossAmountFrom
        ),
        FooterTotalEstimatedGrossAmountTo: this.roundNumber(
          displayData[0].FooterTotalEstimatedGrossAmountTo
        )
      });

      return displayData;
    }
    return [];
  }

  getColumnsWithCustomFilter(columns) {
    const { t } = this.props;
    columns.Indications = {
      ...columns.Indications,
      customFilter: {
        data: [
          {
            name: t('All'),
            default: true,
            value: ''
          },
          {
            name: t('None'),
            default: false,
            value: false,
            field: INDICATION_TRANSPORTATION_FIELD
          },
          ...this.getIndicationColumnFilter()
        ]
      }
    };
    columns.StatusId = {
      ...columns.StatusId,
      customFilter: {
        data: [
          {
            name: t('Select all'),
            value: '',
            default: true
          },
          ...this.props.gridSelections.statuses.map(item => ({
            value: item.id,
            name: item.title
          }))
        ]
      }
    };
    columns.Group = {
      ...columns.Group,
      customFilter: {
        filter: 'contains',
        data: [
          {
            name: t('Select all'),
            default: true,
            value: ''
          },
          ...this.getColumnFilterValuesAsStringNoId(
            GRID_CUSTOM_FILTER_FIELDS.groups,
            'GroupNames'
          )
        ]
      }
    };
    columns.AnnexId = {
      ...columns.AnnexId,
      customFilter: {
        data: [
          {
            name: t('Select all'),
            value: '',
            default: true
          },
          ...this.props.gridSelections.annexes.map(item => ({
            value: `<guid>${item.id}</guid>`,
            name: item.title
          }))
        ]
      }
    };
    columns.UnitOfMeasureId = {
      ...columns.UnitOfMeasureId,
      customFilter: {
        data: [
          {
            name: t('Select all'),
            value: '',
            default: true
          },
          ...this.props.gridSelections.unitsOfMeasure.map(item => ({
            value: `<guid>${item.id}</guid>`,
            name: item.title
          }))
        ]
      }
    };
    columns.Region = {
      ...columns.Region,
      customFilter: {
        filter: 'contains',
        data: [
          {
            name: t('Select all'),
            value: '',
            default: true
          },
          ...this.getColumnFilterValuesAsString(
            GRID_CUSTOM_FILTER_FIELDS.regions,
            'RegionIds'
          )
        ]
      }
    };
    columns.Location = {
      ...columns.Location,
      customFilter: {
        filter: 'contains',
        data: [
          {
            name: t('Select all'),
            value: '',
            default: true
          },
          ...this.getColumnFilterValuesAsStringNoId(
            GRID_CUSTOM_FILTER_FIELDS.locations,
            'LocationFromNames'
          )
        ]
      }
    };
    columns.LocationTo = {
      ...columns.LocationTo,
      customFilter: {
        filter: 'contains',
        data: [
          {
            name: t('Select all'),
            value: '',
            default: true
          },
          ...this.getColumnFilterValuesAsStringNoId(
            GRID_CUSTOM_FILTER_FIELDS.locationsTo,
            'LocationToNames'
          )
        ]
      }
    };

    return columns;
  }

  handleSelect = dataItem => {
    const selectedRows = {
      ...this.state.selectedRows,
      [dataItem.ServiceRequestId]: !this.state.selectedRows[
        dataItem.ServiceRequestId
      ]
    };

    this.setState({
      selectedRows: selectedRows
    });
  };

  handleSelectAll = () => {
    const selectedServiceRequests = this.getSelectedServiceRequestIds();
    const isAllSelected =
      selectedServiceRequests.length === 0 || !this.state.isAllSelected
        ? !this.state.isAllSelected
        : this.state.isAllSelected;

    this.setState({
      isAllSelected: isAllSelected,
      selectedRows: {}
    });
  };

  getSelectedServiceRequestIds = (data, isAllSelected) => {
    if (isAllSelected && data) {
      return data.data.flatMap(item => item.ServiceRequestId);
    }

    const selectedRows = this.state.selectedRows;
    return Object.keys(selectedRows).filter(key => selectedRows[key]);
  };

  openExerciseSelectionDialog = async () => {
    const { oData } = this.props;
    const { isAllSelected } = this.state;

    const selectedServiceRequests = this.getSelectedServiceRequestIds();

    if (
      (!isAllSelected && selectedServiceRequests.length > 0) ||
      (isAllSelected && oData.total !== selectedServiceRequests.length)
    ) {
      this.setState({
        IsExerciseSelectionDialogOpen: true
      });
    }
  };

  getColumnFilterValuesAsStringNoId(gridFilterFieldName, field) {
    const { gridSelections } = this.props;

    if (gridSelections && gridSelections[gridFilterFieldName]) {
      return gridSelections[gridFilterFieldName].map(filterValue => {
        const filter = {
          name: filterValue.title,
          value: filterValue.title
        };

        if (field) {
          filter.field = field;
        }
        return filter;
      });
    }

    return [];
  }

  getColumnFilterValuesAsString(gridFilterFieldName, field) {
    const { gridSelections } = this.props;

    if (gridSelections && gridSelections[gridFilterFieldName]) {
      return gridSelections[gridFilterFieldName].map(filterValue => {
        const filter = {
          name: filterValue.title,
          value: filterValue.id
        };

        if (field) {
          filter.field = field;
        }
        return filter;
      });
    }

    return [];
  }

  applyCustomFilters() {
    this.setState(state => ({
      columns: this.getColumnsWithCustomFilter(state.columns)
    }));
  }

  async componentDidMount() {
    const { partyId } = this.props.match.params;
    await this.queryOData();
    await this.props.getAnnexes();
    await this.props.getRequestedServicesGrid(partyId);
    this.applyCustomFilters();
    await this.props.getSorParty(partyId).then(response => {
      const { statementOfRequirementId } = response.data;
      this.props.getSor(statementOfRequirementId);
    });
    await this.getPartyExercises();
    this.props.withVisibleColumns.setColumns(this.state.columns);
    this.props.withVisibleColumns.onUpdate(() => {
      this.setState({
        gridKey: ShortId.generate()
      });
    });
    window.addEventListener('scroll', this.handleScroll);
    window.addEventListener('resize', this.handleScroll);
  }

  componentWillUnmount() {
    this.props.resetOData();
    this.props.crealSorParty();
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleScroll);
  }

  handleScroll = () => {
    document.getElementsByClassName('k-grid').forEach(grid => {
      const header = grid.getElementsByClassName('k-grid-header');
      if (!header.length) {
        return;
      }

      if (window.pageYOffset >= grid.offsetTop) {
        if (!header[0].classList.contains('fixed-header')) {
          header[0].classList.add('fixed-header');
        }
        header[0].style.maxWidth = `${grid.offsetWidth}px`;
      } else {
        if (header[0].classList.contains('fixed-header')) {
          header[0].classList.remove('fixed-header');
          header[0].style.maxWidth = '';
        }
      }
    });
  };

  exportServiceRequests = async () => {
    await this.getXlsData();
    this.exportRef.current.save();
  };

  getExportFileName = () => {
    return 'Requested services.xlsx';
  };

  setCorStatus = (units = [], key) => {
    units.forEach((unit, index) => {
      if (unit.quantity !== null && unit.units) {
        this.setCorStatus(unit.units, `${key}[${index}].units`);
        this.corStatus.push(true);
      } else {
        this.corStatus.push(false);
      }
    });
  };

  canCreateServiceRequest = () => {
    const { sorParty } = this.props;
    const { isCreateSrDisabled } = this.state;

    if (sorParty) {
      return (
        (sorParty.canCreateServiceRequest ||
          sorParty.canCreateAdditionalServiceRequest) &&
        !isCreateSrDisabled
      );
    }

    return !isCreateSrDisabled;
  };

  canManageApprovals() {
    const { sorParty } = this.props;

    return !!ObjectPath.get(sorParty, 'canManageApprovals');
  }

  dataStateChange = event => {
    this.setState({ dataState: event.dataState }, () => {
      this.queryOData();
    });
  };

  onUserSaveSettings = () => {
    localStorage.setItem(
      this.LSdataStateName,
      JSON.stringify(this.state.dataState)
    );
    localStorage.setItem(
      this.LSGridPropsName,
      JSON.stringify({
        columns: this.grid.current.columns
      })
    );
  };

  onUserResetSettings = onUserResetSettings(
    this.LSdataStateName,
    this.LSGridPropsName,
    this.NotificationsQueryFilterName,
    () => {
      this.props.withVisibleColumns.setColumns(
        Object.assign(
          {},
          this.getColumnsWithCustomFilter(this.defaultGridColumns)
        )
      );

      this.setState(
        {
          gridKey: ShortId.generate(),
          columns: this.getColumnsWithCustomFilter(this.defaultGridColumns),
          dataState: this.defaultDataState
        },
        () => {
          this.queryOData();
        }
      );
    }
  );

  getDataState() {
    let filters = [];

    if (Object.keys(this.props.withQuickSearchFilter).length > 0) {
      filters.push(this.props.withQuickSearchFilter);
    }

    if (this.props.withAnnexFilterSelected) {
      filters.push(this.props.withAnnexFilterOdata('AnnexId'));
    }

    if (this.state.selectedUnit && this.state.selectedUnit != 'All units') {
      filters.push({
        field: 'UnitId',
        operator: 'eq',
        value: `<guid>${this.state.selectedUnit}</guid>`
      });
    }

    const dataState = {
      ...this.state.dataState
    };

    if (
      this.state.dataState.filter &&
      this.state.dataState.filter.filters.length > 0
    ) {
      filters = filters.concat(this.state.dataState.filter);
    }

    if (filters.length > 0) {
      dataState.filter = {
        logic: 'and',
        filters
      };
    }

    return dataState;
  }

  queryOData = () => {
    const dataState = this.getDataState();

    this.props.queryOData(this.getServiceRequestsUrl(), dataState);

    if (this.props.user.PartyTypeId == PARTY_TYPE.SendingNation.Id) {
      localStorage.setItem(
        this.NotificationsQueryFilterName,
        JSON.stringify(this.state.dataState)
      );
    }
  };

  onQuickSearch = quickSearchValue => {
    const { preFilledQuickSearch, dataState } = this.state;
    this.setState(
      {
        dataState: {
          ...dataState,
          skip: preFilledQuickSearch ? dataState.skip : 0
        },
        quickSearchValue,
        preFilledQuickSearch: null
      },
      () => {
        this.queryOData();
      }
    );
  };

  handleUnitFilterChange = value => {
    this.setState(
      {
        selectedUnit: value,
        dataState: {
          ...this.state.dataState,
          skip: 0
        }
      },
      () => {
        this.queryOData();
      }
    );
  };

  closeSorConfirmDialog = () => {
    this.setState({
      showConfirmDialog: false
    });
  };

  flattenUnitsTree(array, level = 0) {
    if (!Array.isArray(array)) {
      return [];
    }

    const units = array.reduce((result, item) => {
      result.push({
        unitName: `${'\xa0\xa0\xa0'.repeat(level)}${item.unitName}`,
        value: item.unitName,
        unitId: item.unitId
      });

      if (Array.isArray(item.units)) {
        result = result.concat(this.flattenUnitsTree(item.units, level + 1));
      }

      return result;
    }, []);

    return units;
  }

  getUnitOptions = unitsTree => {
    const { t } = this.props;

    const units = this.flattenUnitsTree(unitsTree);

    if (units && units.length) {
      return []
        .concat(
          [
            {
              unitName: t('All units'),
              value: ''
            }
          ],
          units
        )
        .map(unit => {
          return (
            <option key={ShortId.generate()} value={unit.unitId}>
              {unit.unitName}
            </option>
          );
        });
    }

    return (
      <option value="">
        {t(
          'There are no SN units added for this Exercise. Please add them in SOR requisites'
        )}
      </option>
    );
  };

  getFieldValue(field, fallbackValue = '-') {
    if (field === '' || field === null) {
      return fallbackValue;
    }

    return field;
  }

  getDiscussionPath(srId) {
    const { match } = this.props;
    return `${match.url}/${srId}/discussion`;
  }

  getSummaryInfo() {
    const { selectedAnnex } = this.state;
    const { currentExercise, t } = this.props;

    const phone = ObjectPath.get(selectedAnnex, 'responsiblePerson.phone')
      ? this.getFieldValue(selectedAnnex.responsiblePerson.phone)
      : this.getFieldValue(
          ObjectPath.get(
            currentExercise,
            'hostInformation.responsiblePersonPhone'
          )
        );
    const name = ObjectPath.get(
      selectedAnnex,
      'responsiblePerson.responsibleName'
    )
      ? this.getFieldValue(selectedAnnex.responsiblePerson.responsibleName)
      : this.getFieldValue(
          ObjectPath.get(
            currentExercise,
            'hostInformation.responsiblePersonName'
          )
        );

    const email = ObjectPath.get(
      selectedAnnex,
      'responsiblePerson.responsibleName'
    )
      ? this.getFieldValue(selectedAnnex.responsiblePerson.email)
      : this.getFieldValue(
          ObjectPath.get(
            currentExercise,
            'hostInformation.responsiblePersonEmail'
          )
        );

    const organization = ObjectPath.get(
      currentExercise,
      'hostInformation.responsiblePersonOrganization'
    )
      ? ObjectPath.get(
          currentExercise,
          'hostInformation.responsiblePersonOrganization'
        ) + t(' responsible')
      : this.getFieldValue(
          ObjectPath.get(
            currentExercise,
            'hostInformation.responsiblePersonOrganization'
          ),
          t('Responsible person')
        );

    return {
      responsiblePerson: {
        phone,
        name,
        email
      },
      requestedQuantity: this.props.oData.total,
      annexName:
        selectedAnnex && selectedAnnex.name ? selectedAnnex.name : null,
      organization,
      sendingNationName: this.props.sorParty.partyName
    };
  }

  async getXlsData() {
    const { t } = this.props;
    const dataState = this.getDataState();
    delete dataState.take;
    dataState.skip = 0;

    const XLSoData = await this.props.queryXLSOData(
      this.getServiceRequestsUrl(),
      dataState
    );

    const data = XLSoData.data.map(item => {
      return {
        ...item,
        DateFrom: parse(item.DateFrom),
        DateTo: parse(item.DateTo),
        StatusIdOriginal: item.StatusId,
        StatusId: item.StatusName,
        IsPaid: item.IsPaid ? t('Yes') : t('No'),
        CreationDate: parse(item.CreationDate),
        EstimatedGrossAmountFrom: item.EstimatedGrossAmountFrom
          ? this.roundNumber(item.EstimatedGrossAmountFrom)
          : item.EstimatedGrossAmountFrom,
        EstimatedGrossAmountTo: item.EstimatedGrossAmountTo
          ? this.roundNumber(item.EstimatedGrossAmountTo)
          : item.EstimatedGrossAmountTo,
        EstimatedTotalCostFrom: item.EstimatedTotalCostFrom
          ? this.roundNumber(item.EstimatedTotalCostFrom)
          : item.EstimatedTotalCostFrom,
        EstimatedTotalCostTo: item.EstimatedTotalCostTo
          ? this.roundNumber(item.EstimatedTotalCostTo)
          : item.EstimatedTotalCostTo
      };
    });

    this.setState({
      XLSoData: data
    });
  }

  getTotalEstimatedTotalCost = () => {
    const { XLSoData } = this.state;

    return {
      FooterTotalActualCost: XLSoData[0].FooterTotalActualCost
        ? this.roundNumber(XLSoData[0].FooterTotalActualCost)
        : XLSoData[0].FooterTotalActualCost,
      FooterTotalCost: XLSoData[0].FooterTotalCost
        ? this.roundNumber(XLSoData[0].FooterTotalCost)
        : XLSoData[0].FooterTotalCost,
      FooterTotalGrossAmount: XLSoData[0].FooterTotalGrossAmount
        ? this.roundNumber(XLSoData[0].FooterTotalGrossAmount)
        : XLSoData[0].FooterTotalGrossAmount,
      FooterTotalEstimatedCostFrom: XLSoData[0].FooterTotalEstimatedCostFrom
        ? this.roundNumber(XLSoData[0].FooterTotalEstimatedCostFrom)
        : XLSoData[0].FooterTotalEstimatedCostFrom,
      FooterTotalEstimatedCostTo: XLSoData[0].FooterTotalEstimatedCostTo
        ? this.roundNumber(XLSoData[0].FooterTotalEstimatedCostTo)
        : XLSoData[0].FooterTotalEstimatedCostTo,
      FooterTotalEstimatedGrossAmountFrom: XLSoData[0]
        .FooterTotalEstimatedGrossAmountFrom
        ? this.roundNumber(XLSoData[0].FooterTotalEstimatedGrossAmountFrom)
        : XLSoData[0].FooterTotalEstimatedGrossAmountFrom,
      FooterTotalEstimatedGrossAmountTo: XLSoData[0]
        .FooterTotalEstimatedGrossAmountTo
        ? this.roundNumber(XLSoData[0].FooterTotalEstimatedGrossAmountTo)
        : XLSoData[0].FooterTotalEstimatedGrossAmountTo
    };
  };

  navigateToSavedLocation(nav) {
    if (
      nav.previousGridPosition.windowScrollOffset &&
      nav.previousGridPosition.navigationOrigin ===
        SERVICE_REQUEST_NAVIGATION_ORIGIN.RequestedServices &&
      this.state.previousWindowScrollOffset !==
        nav.previousGridPosition.windowScrollOffset
    ) {
      this.setState({
        previousWindowScrollOffset: nav.previousGridPosition.windowScrollOffset
      });
      nav.clearPreviousPosition();
    }
  }

  setSavedDataState(nav) {
    const newState = {};
    if (
      nav.previousGridPosition.dataState &&
      nav.previousGridPosition.navigationOrigin !==
        SERVICE_REQUEST_NAVIGATION_ORIGIN.RequestedServices
    ) {
      nav.clearPreviousPosition();
    }

    if (
      nav.previousGridPosition.dataState &&
      nav.previousGridPosition.navigationOrigin ===
        SERVICE_REQUEST_NAVIGATION_ORIGIN.RequestedServices &&
      JSON.stringify(nav.previousGridPosition.dataState) !==
        JSON.stringify(this.state.dataState)
    ) {
      Object.assign(newState, {
        dataState: nav.previousGridPosition.dataState,
        previousWindowScrollOffset: nav.previousGridPosition.windowScrollOffset
      });
    }

    if (
      nav.previousGridPosition.selectedAnnex &&
      JSON.stringify(nav.previousGridPosition.selectedAnnex) !==
        JSON.stringify(this.state.preselectedAnnex)
    ) {
      Object.assign(newState, {
        preselectedAnnex: nav.previousGridPosition.selectedAnnex
      });
      nav.clearSelectedAnnex();
    }
    if (
      nav.previousGridPosition.savedQuickSearch &&
      JSON.stringify(nav.previousGridPosition.savedQuickSearch) !==
        JSON.stringify(this.state.preFilledQuickSearch)
    ) {
      Object.assign(newState, {
        preFilledQuickSearch: nav.previousGridPosition.savedQuickSearch
      });
      nav.clearSavedQuickSearch();
    }
    if (
      nav.previousGridPosition.selectedUnit &&
      nav.previousGridPosition.navigationOrigin ===
        SERVICE_REQUEST_NAVIGATION_ORIGIN.RequestedServices
    ) {
      Object.assign(newState, {
        selectedUnit: nav.previousGridPosition.selectedUnit
      });
      nav.clearSelectedUnit();
    }

    if (JSON.stringify(newState) !== JSON.stringify({})) {
      this.setState(newState, () => {
        this.queryOData();
      });
    }

    if (nav.previousGridPosition && nav.previousGridPosition.dataState) {
      nav.clearSavedDataState();
    }
  }

  clearSavedOffset() {
    if (
      this.state.previousWindowScrollOffset &&
      window.pageYOffset === this.state.previousWindowScrollOffset
    ) {
      this.setState({
        previousWindowScrollOffset: null
      });
    }
  }

  scrollWindow() {
    this.state.previousWindowScrollOffset &&
      window.scrollTo(0, this.state.previousWindowScrollOffset);
  }
}

ServiceRequests.propTypes = {
  isLoading: PropTypes.bool,
  queryOData: PropTypes.func,
  queryXLSOData: PropTypes.func,
  resetOData: PropTypes.func,
  withQuickSearchForm: PropTypes.func,
  withAnnexFilterForm: PropTypes.func,
  getAnnexes: PropTypes.func,
  oData: PropTypes.object,
  withAnnexFilterSelected: PropTypes.string,
  withAnnexFilterOdata: PropTypes.func,
  withQuickSearchFilter: PropTypes.object,
  withVisibleColumns: PropTypes.object,
  serviceRequests: PropTypes.object,
  sor: PropTypes.object,
  t: PropTypes.func,
  match: PropTypes.object,
  location: PropTypes.object,
  confirmSor: PropTypes.func,
  getSorServiceRequest: PropTypes.func,
  currentExercise: PropTypes.object,
  gridSelections: PropTypes.object,
  sorParty: PropTypes.object,
  getSorParty: PropTypes.func,
  getSor: PropTypes.func,
  crealSorParty: PropTypes.func,
  hasUserPermission: PropTypes.func,
  getRequestedServicesGrid: PropTypes.func,
  user: PropTypes.object
};

export default ServiceRequests;
